import { forEach, groupBy, keyBy, mapValues, pickBy } from 'lodash'
import { storeToRefs } from 'pinia'

import api from '../api/index'
import * as mutationTypes from './mutation-types'
import remarkTags from './remark-tags'
import { SfLog } from '@app/adapters/logger'

import { useAuditsStore } from '@app/application/sidebar-audit-log/store/audits'
import { useCommsFilterStore } from '@app/application/communication/store/comms-filters'
import { useContext } from '@app/shared/pinia/context'

// this contains shared logic included in multiple stores, in the root of the store (not as a submodule)
export default (endpointParams, initialData) => {
  return {
    getters: {
      hasContext: (state) => {
        return state.contextType !== undefined ? true : false
      },

      ledgerId: (state) => {
        return state.ledgerId
      },

      communicationItemType: (state) => {
        return state.communicationItemType
      },

      communicationItemId: (state) => {
        return state.communicationItemId
      },

      contextType: (state) => {
        return state.contextType
      },

      contextHrefParam: (state) => {
        return state.contextHrefParam
      },

      contextId: (state) => {
        return state.contextId
      },
      // filter Remarks by workflow review active tag
      filteredNotes: (_state, getters) => {
        const filterStore = useCommsFilterStore()
        const { typeFilter } = storeToRefs(filterStore)

        if (typeFilter.value !== null && typeFilter.value !== 'note') return {}

        return getters.filteredRemarks('notes')
      },
      filteredTodos: (_state, getters) => {
        const filterStore = useCommsFilterStore()
        const { typeFilter } = storeToRefs(filterStore)

        if (typeFilter.value !== null && typeFilter.value !== 'todo') return {}

        return getters.filteredRemarks('todos')
      },
      filteredSfAssistantRemarks: (state, getters) => {
        return getters.filteredRemarks('sfAssistantCheckTodos')
      },
      filteredRemarks: (state, getters) => (remarkType) => {
        const remarks = getters[remarkType]

        const { workflowReviewActiveTagId } = state.remarkTags

        if (workflowReviewActiveTagId) {
          return pickBy(remarks, (remark) =>
            remark.tagIds.includes(workflowReviewActiveTagId)
          )
        }

        return remarks
      },
      workflowReviewActiveTag: (state) => {
        return state.remarkTags.workflowReviewTags.find(
          (t) => t.id === state.remarkTags.workflowReviewActiveTagId
        )
      },
      filteredCheckLists: (state) => {
        const filterStore = useCommsFilterStore()
        const { typeFilter } = storeToRefs(filterStore)

        if (typeFilter.value !== null && typeFilter.value !== 'checklist')
          return {}

        return state.checks.checkLists[state.ledgerId] || {}
      },
    },

    mutations: {
      [mutationTypes.SET_COMMUNICATION_CONTEXT](
        state,
        { contextType, contextId, contextHrefParam }
      ) {
        state.contextType = contextType
        state.contextId = contextId
        state.contextHrefParam = contextHrefParam
      },
      [mutationTypes.SET_LOADING](state, { type, value }) {
        state.loading[type] = value
      },
    },

    actions: {
      async fetchCommunication({ commit, state }, { action, remark } = {}) {
        commit(mutationTypes.START_FETCHING_COMMUNICATIONS)

        try {
          const data = await api.getCommunication(this.state)

          commit(mutationTypes.STOP_FETCHING_COMMUNICATIONS)
          const communication = data?.communication

          if (!communication) {
            throw new Error('getCommunication response returned empty')
          }

          // key contexts by type (Account, ReconciliationText...) and then by id
          // to avoid id collisions of different type
          const contexts = mapValues(
            groupBy(communication.contexts, 'ledgerId'),
            (v) => mapValues(groupBy(v, 'type'), (v) => keyBy(v, 'id'))
          )
          commit(mutationTypes.SET_CONTEXTS, contexts)

          if (remark) {
            commit(mutationTypes.REMARK_CHANGED, { action, remark })
          }

          commit(mutationTypes.SET_REMARKS, {
            ...keyBy(communication.notes, 'id'),
            ...keyBy(communication.todos, 'id'),
            ...keyBy(communication.sfAssistantCheckTodos, 'id'),
          })

          // index check ids for check lists on the front-end for now
          const checkLists = mapValues(
            groupBy(communication.checkLists, 'ledgerId'),
            (v) => keyBy(v, 'id')
          )
          forEach(checkLists, (clGroup) =>
            forEach(clGroup, (cl) => {
              cl['checkIds'] = []
              for (const check of communication.checks) {
                if (check.checkListId === cl.id) {
                  cl['checkIds'].push(check.id)
                }
              }
            })
          )
          commit(mutationTypes.SET_CHECK_LISTS, checkLists)
          commit(mutationTypes.SET_CHECKS, keyBy(communication.checks, 'id'))
          commit(mutationTypes.SET_USERS, keyBy(communication.users, 'id'))
          commit(
            mutationTypes.SET_ATTACHMENTS,
            keyBy(communication.attachments, 'id')
          )
          commit(
            mutationTypes.SET_COMMENTS,
            keyBy(communication.comments, 'id')
          )
          commit(
            mutationTypes.SET_CHECKINS,
            keyBy(communication.checkins, 'id')
          )

          const allRemarkTags = keyBy(communication.allRemarkTags, 'id')
          commit(mutationTypes.SET_ALL_REMARK_TAGS, allRemarkTags)

          // TODO: quite hacky, find a better way to pass all-remark-tags to live-inputs (jquery-based)
          window.allRemarkTags = allRemarkTags
        } catch (e) {
          SfLog.warn('Failed to fetch communication', {
            extra: {
              error: e,
              ledgerId: state.ledgerId,
              ledgerPeriodName: state.ledgerPeriodName,
              communicationItemId: state.communicationItemId,
            },
          })
        }
      },

      fetchMentionUsernames({ commit }) {
        return api
          .getMentionUsernames({
            companyId:
              this.state.companyId || this.state.notifications.activeCompanyId,
          })
          .then((data) => {
            commit(mutationTypes.SET_MENTION_USERNAMES, data)
          })
      },

      async fetchTimelineData({ commit }) {
        try {
          const data = await api.getTimelineData(this.state)

          const serialized = data.timeline
          const deserialized = {}
          deserialized.timespanStart = new Date(serialized.timespanStart)
          deserialized.timespanEnd = new Date(serialized.timespanEnd)
          deserialized.bookyearEndDates = serialized.bookyearEndDates.map(
            (d) => {
              return new Date(d)
            }
          )
          deserialized.ledgers = serialized.ledgers.map((ledger) => {
            return {
              ledgerId: ledger.ledgerId,
              ledgerEndDate: new Date(ledger.ledgerEndDate),
              ledgerPeriodName: ledger.ledgerPeriodName,
              stickyNotesCount: ledger.stickyNotesCount,
              stickyTodosCount: ledger.stickyTodosCount,
              notesCount: ledger.notesCount,
              todosCount: ledger.todosCount,
              checksCount: ledger.checksCount,
              openTodos: ledger.openTodos,
              openChecks: ledger.openChecks,
            }
          })
          commit(mutationTypes.SET_TIMELINE_DATA, deserialized)
        } catch (e) {
          SfLog.warn('Failed to fetch timeline data', {
            extra: {
              error: e,
              ...this.state,
            },
          })
        }
      },

      // Reset all communication data to it's default values
      resetCommunicationData({ commit }) {
        const filterStore = useCommsFilterStore()

        commit(mutationTypes.SET_REMARKS, {})
        commit(mutationTypes.SET_CHECK_LISTS, {})
        commit(mutationTypes.SET_CHECKS, {})
        commit(mutationTypes.SET_USERS, {})
        commit(mutationTypes.SET_ATTACHMENTS, {})
        commit(mutationTypes.SET_COMMENTS, {})
        commit(mutationTypes.SET_CHECKINS, {})

        filterStore.clearTypeFilter()
      },

      setCommunicationContext({ commit, dispatch }, communicationContext) {
        const auditsStore = useAuditsStore()
        const contextStore = useContext()

        commit(mutationTypes.SET_COMMUNICATION_CONTEXT, communicationContext)
        commit(mutationTypes.RESET_FETCHING_COMMUNICATIONS)
        dispatch('fetchCommunication')
        dispatch('unsetHighlightedCard')

        // TODO: Split the context out of the comms store entirely
        // Until that is done keep a duplicate copy of it
        contextStore.setContext(
          communicationContext.contextType,
          communicationContext.contextId
        )

        // Eagerly load the changes since last visit to display the appropriate unread count UI, setCommunicationContext is used in Workflow Review only
        auditsStore.fetchChangesSinceLastVisit({
          contextId: communicationContext.contextId,
          contextType: communicationContext.contextType,
        })
      },

      remarkChanged({ commit }, { action, remark }) {
        commit(mutationTypes.REMARK_CHANGED, { action, remark })
      },
    },

    state() {
      const { tagCloud } = initialData

      return {
        contextType: endpointParams.contextType,
        contextId: endpointParams.contextId,
        ledgerId: endpointParams.ledgerId,
        ledgerPeriodName: endpointParams.ledgerPeriodName,
        contextHrefParam: endpointParams.contextHrefParam,
        communicationItemId: endpointParams.communicationItemId,
        communicationItemType: endpointParams.communicationItemType,
        pulseScope: endpointParams.pulseScope,
        companyNotificationsScope: endpointParams.companyNotificationsScope,
        remarkTags: {
          ...remarkTags.state(),
          workflowReviewTags: tagCloud || [],
        },
        loading: {
          notifications: false,
        },
      }
    },
  }
}
